/**
 * Sprint S13.1.A — onboarding wizard state.
 *
 * Mirrors backend Pydantic OnboardingSubmit shape (Phase E). Persists к
 * sessionStorage so мid-session refresh keeps draft. On mount, OnboardingPage
 * hydrates from `GET /api/onboarding/profile` and jumps к first incomplete
 * step. Final submit posts к `POST /api/onboarding/complete` (idempotent).
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";


export interface TypicalSessionLength {
  weekday_min: number;
  weekday_max: number;
  weekend_min: number;
  weekend_max: number;
}


// Mirror backend `OnboardingSubmit` Pydantic shape (Phase E)
export interface OnboardingData {
  // Identity
  first_name: string | null;
  sex_biological: "male" | "female" | "prefer_not_to_say";
  date_of_birth: string | null;  // ISO date YYYY-MM-DD

  // Phase B health context
  health_conditions: string[];
  medications: string[];
  smoking_status: "never" | "former" | "current" | "prefer_not_to_say";
  alcohol_consumption:
    | "none" | "occasional" | "weekly" | "daily" | "prefer_not_to_say";
  mental_health_flags: string[];
  pregnancy_status:
    | "not_applicable"
    | "not_pregnant"
    | "pregnant_t1" | "pregnant_t2" | "pregnant_t3"
    | "postpartum_0_6mo" | "postpartum_6_12mo";
  informed_consent_signed: boolean;

  // Phase E lifestyle
  training_facilities: string[];
  preferred_training_time:
    | "morning_5_9" | "morning_6_10"
    | "afternoon_12_17" | "evening_17_21"
    | "split_am_pm" | "flexible";
  typical_session_length_min: TypicalSessionLength;
  active_sports: string[];
  equipment: string[];
  primary_goals: string[];
}


export const DEFAULT_ONBOARDING: OnboardingData = {
  first_name: null,
  sex_biological: "prefer_not_to_say",
  date_of_birth: null,
  health_conditions: [],
  medications: [],
  smoking_status: "prefer_not_to_say",
  alcohol_consumption: "prefer_not_to_say",
  mental_health_flags: [],
  pregnancy_status: "not_applicable",
  informed_consent_signed: false,
  training_facilities: [],
  preferred_training_time: "flexible",
  typical_session_length_min: {
    weekday_min: 30,
    weekday_max: 60,
    weekend_min: 60,
    weekend_max: 120,
  },
  active_sports: [],
  equipment: [],
  primary_goals: [],
};


// Step IDs — used by step components к identify themselves в state machine
export type StepId =
  | "welcome"
  | "health_context"
  | "lifestyle"
  | "mental_pregnancy"
  | "sport_profile"
  | "schedule"
  | "goals"
  | "consent_submit";

export const STEP_ORDER: StepId[] = [
  "welcome",
  "health_context",
  "lifestyle",
  "mental_pregnancy",
  "sport_profile",
  "schedule",
  "goals",
  "consent_submit",
];


interface OnboardingState {
  data: OnboardingData;
  currentStepIndex: number;
  isHydrated: boolean;
  isSubmitting: boolean;
  submitError: string | null;

  setField: <K extends keyof OnboardingData>(
    field: K,
    value: OnboardingData[K],
  ) => void;
  setData: (partial: Partial<OnboardingData>) => void;
  goToStep: (index: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setHydrated: (hydrated: boolean) => void;
  setSubmitting: (submitting: boolean) => void;
  setSubmitError: (err: string | null) => void;
  reset: () => void;
}


export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      data: DEFAULT_ONBOARDING,
      currentStepIndex: 0,
      isHydrated: false,
      isSubmitting: false,
      submitError: null,

      setField: (field, value) =>
        set((state) => ({ data: { ...state.data, [field]: value } })),

      setData: (partial) =>
        set((state) => ({ data: { ...state.data, ...partial } })),

      goToStep: (index) =>
        set(() => ({
          currentStepIndex: Math.max(
            0,
            Math.min(STEP_ORDER.length - 1, index),
          ),
        })),

      nextStep: () =>
        set((state) => ({
          currentStepIndex: Math.min(
            STEP_ORDER.length - 1,
            state.currentStepIndex + 1,
          ),
        })),

      prevStep: () =>
        set((state) => ({
          currentStepIndex: Math.max(0, state.currentStepIndex - 1),
        })),

      setHydrated: (hydrated) => set({ isHydrated: hydrated }),
      setSubmitting: (submitting) => set({ isSubmitting: submitting }),
      setSubmitError: (err) => set({ submitError: err }),

      reset: () =>
        set({
          data: DEFAULT_ONBOARDING,
          currentStepIndex: 0,
          isHydrated: false,
          isSubmitting: false,
          submitError: null,
        }),
    }),
    {
      name: "mavinty-onboarding-draft",
      storage: createJSONStorage(() => sessionStorage),
      // Persist data + currentStepIndex only; isHydrated re-fetched per session
      partialize: (state) => ({
        data: state.data,
        currentStepIndex: state.currentStepIndex,
      }),
    },
  ),
);


/**
 * Determine the first step that still needs filling. Used by hydration к
 * jump к where user left off. Conservative: only "welcome" and "consent"
 * have hard required fields; intermediate steps allow empty arrays as
 * legitimate ("user has nothing к declare").
 */
export function findFirstIncompleteStep(data: OnboardingData): number {
  // Step 0 (welcome): first_name + DOB required
  if (!data.first_name || !data.date_of_birth) return 0;

  // Step 7 (consent_submit): informed_consent_signed required
  if (!data.informed_consent_signed) return STEP_ORDER.length - 1;

  // Everything filled — sit at last step (user can revisit any step via prev)
  return STEP_ORDER.length - 1;
}
